import { Formik } from "formik";

import { InfoRounded } from "@mui/icons-material";
import { Alert, Box, LinearProgress, Switch } from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import AutocompleteFilter from "@components/Forms/FormInputs/AutocompleteFilter";
import TextField from "@components/Forms/FormInputs/TextField";
import FormLabel from "@components/Forms/FormLabel/Index";

import { IFetchCompanySearch } from "@interfaces/ipsearch.type";

import { useClearSearch, useLoadCompanySearch } from "@queries/ipSearch.query";

import { useTableStore } from "@store/filters/filters.store";
import { PopupActions } from "@store/popupStore";
import useSmartSearchStore from "@store/smartSearch";
import { UserActions, useUserStore } from "@store/userStore";

interface ISmartSearchFormProperties {
  fetchCompanySearch: ({ preferredIndustries, queryString }: IFetchCompanySearch) => void;
}

interface ISearchFormValues {
  isSmartSearchEnabled: boolean;
  preferredIndustries?: string[];
  technologyDescription: string;
}

type ProgressColorType = "error" | "info" | "inherit" | "primary" | "secondary" | "success" | "warning";

const SmartSearchForm = ({ fetchCompanySearch }: ISmartSearchFormProperties) => {
  const user = useUserStore();
  const { mutateAsync: clearSearch } = useClearSearch();

  const { setTableSort } = useTableStore("company");

  const {
    isSmartSearchEnabled,
    isSmartSearchFromTechnologyOverview,
    setIsSmartSearchEnabled,
    setIsSmartSearchFromTechnologyOverview,
    setSmartSearchDescription,
    setTechnologyDescription,
    technologyDescription,
  } = useSmartSearchStore();

  const { data: currentSearch, status: isSearchingLoadingStatus } = useLoadCompanySearch(user.currentSearch ?? "new");

  const doesSearchExist = !!(user.currentSearch && user.currentSearch !== "new");

  //* We are preloading current search but there are instances where users might open the modal before the search is done
  //* so we need to check if the search is done before we can show the modal
  //* this is why we check `isSearchingLoadingStatus === "pending" && user.currentSearch && user.currentSearch !== "new"`
  return (
    <Box sx={{ width: "700px" }}>
      {isSearchingLoadingStatus === "pending" && doesSearchExist ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
          <Typography
            fontWeight="700"
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              justifyContent: "center",
              mt: 2,
            }}
          >
            <span>One moment!</span>
            <span> Tailoring your search results.</span>
          </Typography>
        </Box>
      ) : (
        <>
          <Formik
            initialValues={
              {
                isSmartSearchEnabled,
                preferredIndustries: technologyDescription ? [] : currentSearch?.preferredIndustries ?? [],
                technologyDescription: technologyDescription || (currentSearch?.queryString ?? ""),
              } as ISearchFormValues
            }
            onSubmit={(values) => {
              setIsSmartSearchEnabled(true);
              setSmartSearchDescription(values.technologyDescription);

              void fetchCompanySearch({
                preferredIndustries: values.preferredIndustries,
                queryString: values.technologyDescription,
              });
              localStorage.setItem("selectedStatus", JSON.stringify(["Application", "Granted"]));
              PopupActions.closeModal();
            }}
          >
            {({ setValues, submitForm, values }) => {
              const descriptionLength = values.technologyDescription
                .replaceAll(/[^\d\sA-Za-z]/g, " ")
                .trim()
                .split(/\s+/)
                .filter(Boolean).length;
              let progressColor: ProgressColorType = "warning";
              let progressValue = 0;
              let progressDescription;

              const targetLength = 110;
              progressValue = Math.min(Math.round((descriptionLength / targetLength) * 100), 100);

              if (descriptionLength < 60) {
                progressColor = "warning";
                progressDescription =
                  "Your description is too brief. Broader inputs return generic results. Try adding specific keywords, features, or applications to improve results.";
              } else if (descriptionLength < 110) {
                progressColor = "info";
                progressDescription =
                  "You're on the right track! A more detailed description will improve match scores for patent similarity.";
              } else {
                progressColor = "success";
                progressDescription =
                  "Great! This description should be long enough for keyword + semantic comparison across patent abstracts and claims.";
              }

              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <Box sx={{ p: 0, width: "100%" }}>
                    <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, mb: 3 }}>
                      <InfoRounded color="info" />
                      <Typography variant="body2">
                        All information is private, secure, and{" "}
                        <b>
                          <u>never</u>
                        </b>{" "}
                        used for model training.
                      </Typography>
                    </Box>
                    <FormLabel
                      hasValue={!!values.technologyDescription}
                      label="Technology Description"
                      name="technologyDescription"
                      sx={{ mb: 1.5 }}
                    />

                    <TextField
                      isFullWidth
                      isMultiline={true}
                      isRequired
                      name="technologyDescription"
                      rows={8}
                      size="small"
                      sx={{
                        "& .MuiInputBase-root": {
                          borderRadius: "8px",
                        },
                        m: 0,
                      }}
                      type="text"
                      variant="outlined"
                    />
                    {descriptionLength > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress
                          color={progressColor}
                          sx={{ borderRadius: 2 }}
                          value={progressValue}
                          variant="determinate"
                        />
                        <Box sx={{ alignItems: "center", display: "flex", gap: 0.75, mt: 2 }}>
                          <Alert severity={progressColor}>{progressDescription}</Alert>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ p: 0, width: "100%" }}>
                    <FormLabel
                      hasValue={!!values.technologyDescription}
                      label="Target Industries"
                      name="weightByIndustry"
                      tooltip="Type the name of an industry and press Enter to add it. Search results will rank these industries higher."
                    />
                    <AutocompleteFilter
                      color="secondary"
                      isDisablePortal={false}
                      isFullWidth
                      isMultiple
                      limitTags={2}
                      name="preferredIndustries"
                      size="small"
                      sx={{
                        "& .MuiFormControl-root": {
                          m: 0,
                        },
                      }}
                      type="text"
                      value={values.preferredIndustries}
                    />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", gap: 1, p: 0, width: "100%" }}>
                      <Button
                        color="primary"
                        disabled={values.technologyDescription.trim() === ""}
                        onClick={() => {
                          void setValues((previousState) => ({
                            ...previousState,
                            smartSearchEnabled: true,
                          }));
                          if (isSmartSearchFromTechnologyOverview) {
                            setIsSmartSearchFromTechnologyOverview(false);
                          }
                          setTechnologyDescription(null);
                          void submitForm();
                        }}
                        sx={{
                          borderRadius: "8px",
                        }}
                        variant="contained"
                      >
                        Search
                      </Button>
                      <Button
                        color="error"
                        disabled={!doesSearchExist}
                        onClick={() => {
                          void setValues({
                            isSmartSearchEnabled: false,
                            preferredIndustries: [],
                            technologyDescription: "",
                          });
                          setIsSmartSearchEnabled(false);
                          void clearSearch();
                          UserActions.setCurrentSearch("new");
                        }}
                        sx={{
                          borderRadius: "8px",
                        }}
                        variant="contained"
                      >
                        Clear
                      </Button>
                    </Box>
                    {doesSearchExist && (
                      <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                        <Typography
                          fontWeight={700}
                          sx={{
                            textWrap: "nowrap",
                          }}
                          variant="body1"
                        >
                          Portfolio Search {values.isSmartSearchEnabled ? "Enabled" : "Disabled"}
                        </Typography>
                        <Switch
                          checked={values.isSmartSearchEnabled}
                          disabled={!doesSearchExist}
                          name="smartSearchEnabled"
                          onChange={(_, checked) => {
                            void setValues((previous) => ({ ...previous, isSmartSearchEnabled: checked }));
                            setIsSmartSearchEnabled(checked);
                            if (checked) {
                              setTableSort([{ field: "similarityScore", sort: "desc" }]);
                            } else {
                              setTableSort([{ field: "numEmployees", sort: "desc" }]);
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
          </Formik>
        </>
      )}
    </Box>
  );
};

export default SmartSearchForm;
